/**
 * useDuplicatePricingRule — "Duplicate this promo" behaviour, shared by the
 * coupon hero and the automatic-rule summary.
 *
 * Reuses the exact create path the wizard ships (useCreatePricingRule →
 * erp.pricingRules.create), which already navigates to the new rule's detail
 * page on success. We only add the clone payload (buildDuplicatePayload) plus
 * loading / success / error toasts. No new endpoint, no rebuilt create logic.
 */

import { toast } from "sonner";
import { useCreatePricingRule } from "@/features/inventory/api/useCreatePricingRule";
import { buildDuplicatePayload, type Lang } from "./pricingRuleDetailModel";

const pick = (lang: Lang, th: string, en: string) => (lang === "th" ? th : en);

export function useDuplicatePricingRule(formData: Record<string, any>, lang: Lang) {
  const mutation = useCreatePricingRule();

  const duplicate = () => {
    if (mutation.isPending) return;
    const payload = buildDuplicatePayload(formData);
    const toastId = toast.loading(pick(lang, "กำลังทำสำเนา...", "Duplicating..."));
    mutation.mutate(payload, {
      onSuccess: () =>
        toast.success(
          pick(
            lang,
            "สร้างสำเนาแล้ว (ปิดใช้งานอยู่ — ตรวจสอบก่อนเปิด)",
            "Draft copy created (disabled — review before turning it on)",
          ),
          { id: toastId },
        ),
      onError: (err: unknown) =>
        toast.error(
          pick(lang, "ทำสำเนาไม่สำเร็จ", "Duplicate failed") +
            (err instanceof Error && err.message ? `: ${err.message}` : ""),
          { id: toastId },
        ),
    });
  };

  return { duplicate, isPending: mutation.isPending };
}
